// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useEffect, useRef, useState } from "react";

import { isSignedOutError, onAuthTokenAvailable } from "../../../services/api/demoData";
import { useSessionExpiredSignal } from "../../../utils/sessionExpirySignal";

export interface SignedOutRead {
  isSignedOut: boolean;
  noteReadError: (error: unknown) => boolean;
  clearSignedOut: () => void;
}

export function useSignedOutRead(): SignedOutRead {
  const [isSignedOut, setIsSignedOut] = useState(false);

  const noteReadError = useCallback((error: unknown): boolean => {
    if (!isSignedOutError(error)) {
      return false;
    }
    setIsSignedOut(true);
    return true;
  }, []);

  const clearSignedOut = useCallback(() => setIsSignedOut(false), []);

  return { isSignedOut, noteReadError, clearSignedOut };
}

export function useRefetchWhenSignedIn(isSignedOut: boolean, reload: () => unknown): void {
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  const isSessionExpired = useSessionExpiredSignal();
  const wasSessionExpiredRef = useRef(isSessionExpired);
  useEffect(() => {
    const wasExpired = wasSessionExpiredRef.current;
    wasSessionExpiredRef.current = isSessionExpired;
    if (isSignedOut && wasExpired && !isSessionExpired) {
      void reloadRef.current();
    }
  }, [isSignedOut, isSessionExpired]);

  useEffect(() => {
    if (!isSignedOut) {
      return undefined;
    }
    return onAuthTokenAvailable(() => {
      void reloadRef.current();
    });
  }, [isSignedOut]);
}
